/**
 * EN / ES translations for the /about page.
 * Imported and merged into the main translations object.
 */
export const aboutTranslations = {
  en: {
    about: {
      eyebrow: "About Kranbos",
      title: "Senior engineering, pointed at local service businesses",
      subtitle:
        "Kranbos is a small, senior team led by a software engineer who has spent 14+ years building platforms and internal tools for large companies. We apply that same discipline to the everyday systems local service businesses depend on.",
      primaryCta: "Free 15-Minute Lead Leak Check",
      secondaryCta: "Contact Us",

      founderEyebrow: "Founder",
      founderName: "Giuliano Kranevitter",
      founderRole: "Founder & Principal Engineer — Miami, FL",
      founderBio1:
        "I have spent my career building developer platforms, internal tooling, and high-traffic web applications — currently as a senior engineer at a Fortune 100 financial services company, where I build and maintain an internal React and Node.js framework that powers its customer-facing digital products.",
      founderBio2:
        "Kranbos exists because the same problems show up in local service businesses, only smaller and more expensive: leads that fall through the cracks, manual work nobody has time for, and no clear view of what is actually happening week to week. Those are engineering problems, and they are fixable.",
      founderBio3:
        "Every engagement is handled by senior engineers. You are not handed off to a junior team after the sale.",
      founderPhotoAlt: "Giuliano Kranevitter, founder of Kranbos",

      statsTitle: "Background in numbers",
      stat1Value: "14+",
      stat1Label: "Years building software professionally",
      stat2Value: "30+",
      stat2Label: "Markets served by a component library we led",
      stat3Value: "22%",
      stat3Label: "Throughput gain from a framework performance rebuild",
      stat4Value: "2",
      stat4Label: "Languages: English and Spanish, natively bilingual",

      experienceTitle: "Where that experience comes from",
      experienceText1:
        "Most of that career has been spent inside Fortune 100 companies, national brands, and global digital agencies — building internal developer platforms, high-traffic websites, real-time search over hundreds of millions of records, and booking and payment experiences used by millions of customers.",
      experienceText2:
        "It also means production systems, performance work, code reviews, and on-call responsibility when things break. That is a very different background from a marketing agency that learned to automate, and it is why the systems we install for local businesses are built to keep running without being babysat.",
      experienceLogosLabel: "Companies and brands worked with",
      logoAltAmex: "American Express",
      logoAltCondeNast: "Condé Nast",
      logoAltSapientNitro: "SapientNitro",
      logoAltMetrostudio: "Metrostudio",

      principlesTitle: "How we work",
      principle1Title: "We work around your tools",
      principle1Text:
        "No CRM migration, no new software for your crew to learn. We add automation around what you already use.",
      principle2Title: "Measured, not promised",
      principle2Text:
        "Every system we install reports what it recovered, so you can see the return instead of taking our word for it.",
      principle3Title: "We own the setup",
      principle3Text:
        "Configuration, testing, and monitoring are on us. You stay focused on the jobs.",
      principle4Title: "Straight answers",
      principle4Text:
        "If automation is not the right fix for your problem, we will tell you that on the first call.",

      whoTitle: "Who we work with",
      whoText:
        "Local service businesses in the US that already generate demand and lose part of it to process gaps — typically owner-operated teams handling calls, estimates, and follow-up between jobs. We work in English and Spanish.",

      ctaTitle: "Want to see where your leads are leaking?",
      ctaText:
        "Book a free 15-minute Lead Leak Check. You will get a clear read on where jobs are being lost, whether or not you work with us.",
    },
  },
  es: {
    about: {
      eyebrow: "Sobre Kranbos",
      title: "Ingeniería senior aplicada a negocios de servicios locales",
      subtitle:
        "Kranbos es un equipo pequeño y senior liderado por un ingeniero de software con más de 14 años construyendo plataformas y herramientas internas para grandes empresas. Aplicamos esa misma disciplina a los sistemas del día a día de los negocios de servicios locales.",
      primaryCta: "Chequeo Gratis de Fugas de Leads en 15 Minutos",
      secondaryCta: "Contáctanos",

      founderEyebrow: "Fundador",
      founderName: "Giuliano Kranevitter",
      founderRole: "Fundador e Ingeniero Principal — Miami, FL",
      founderBio1:
        "He dedicado mi carrera a construir plataformas para desarrolladores, herramientas internas y aplicaciones web de alto tráfico — actualmente como ingeniero senior en una empresa Fortune 100 de servicios financieros, donde construyo y mantengo un framework interno de React y Node.js que impulsa sus productos digitales de cara al cliente.",
      founderBio2:
        "Kranbos existe porque los mismos problemas aparecen en los negocios de servicios locales, solo más chicos y más caros: leads que se pierden, trabajo manual que nadie tiene tiempo de hacer y ninguna visibilidad real de lo que pasa cada semana. Son problemas de ingeniería, y se pueden resolver.",
      founderBio3:
        "Cada proyecto lo atienden ingenieros senior. No te pasan a un equipo junior después de la venta.",
      founderPhotoAlt: "Giuliano Kranevitter, fundador de Kranbos",

      statsTitle: "La trayectoria en números",
      stat1Value: "14+",
      stat1Label: "Años construyendo software profesionalmente",
      stat2Value: "30+",
      stat2Label: "Mercados atendidos por una librería de componentes que lideramos",
      stat3Value: "22%",
      stat3Label: "Mejora de throughput en una reconstrucción de performance",
      stat4Value: "2",
      stat4Label: "Idiomas: inglés y español, bilingüe nativo",

      experienceTitle: "De dónde viene esa experiencia",
      experienceText1:
        "Gran parte de esa carrera fue dentro de empresas Fortune 100, marcas nacionales y agencias digitales globales — construyendo plataformas internas para desarrolladores, sitios de alto tráfico, búsqueda en tiempo real sobre cientos de millones de registros y experiencias de reserva y pago usadas por millones de clientes.",
      experienceText2:
        "También significa sistemas en producción, trabajo de performance, revisiones de código y guardias on-call cuando algo se rompe. Es un background muy distinto al de una agencia de marketing que aprendió a automatizar, y por eso los sistemas que instalamos para negocios locales están hechos para seguir funcionando sin que haya que cuidarlos.",
      experienceLogosLabel: "Empresas y marcas con las que trabajamos",
      logoAltAmex: "American Express",
      logoAltCondeNast: "Condé Nast",
      logoAltSapientNitro: "SapientNitro",
      logoAltMetrostudio: "Metrostudio",

      principlesTitle: "Cómo trabajamos",
      principle1Title: "Nos adaptamos a tus herramientas",
      principle1Text:
        "Sin migrar el CRM ni software nuevo que tu equipo tenga que aprender. Agregamos automatización alrededor de lo que ya usas.",
      principle2Title: "Medido, no prometido",
      principle2Text:
        "Cada sistema que instalamos reporta lo que recuperó, así ves el retorno en vez de creernos de palabra.",
      principle3Title: "Nos hacemos cargo del setup",
      principle3Text:
        "La configuración, las pruebas y el monitoreo corren por nuestra cuenta. Tú te enfocas en los trabajos.",
      principle4Title: "Respuestas directas",
      principle4Text:
        "Si la automatización no es la solución para tu problema, te lo decimos en la primera llamada.",

      whoTitle: "Con quién trabajamos",
      whoText:
        "Negocios de servicios locales en Estados Unidos que ya generan demanda y pierden parte de ella por fallas de proceso — típicamente equipos operados por sus dueños que manejan llamadas, presupuestos y seguimiento entre trabajos. Trabajamos en inglés y español.",

      ctaTitle: "¿Quieres ver por dónde se fugan tus leads?",
      ctaText:
        "Agenda un Chequeo de Fugas de Leads gratis de 15 minutos. Vas a salir con una lectura clara de dónde se pierden trabajos, trabajes con nosotros o no.",
    },
  },
};
